import React, { useState } from 'react';
import { ArrowLeft, Bell, Calendar, ShoppingBag, Star, Gift, Check, CheckCheck, Trash2, Settings } from 'lucide-react';
import { useI18n } from '../i18n';
import { useApp } from '../context/AppContext';

// Notifications placeholder - sarà integrato con backend
const NOTIFICATIONS_DATA = [];

const ICON_MAP = {
  booking_confirmed: { icon: Calendar, color: 'emerald' },
  order_shipped: { icon: ShoppingBag, color: 'blue' },
  promo: { icon: Gift, color: 'purple' },
  review: { icon: Star, color: 'amber' },
};

const NotificationsPage = ({ onBack, onNavigate }) => {
  const { t } = useI18n();
  const { user } = useApp();
  
  // Get notifications for current user, or all if no user
  const userNotifications = NOTIFICATIONS_DATA.filter(n => !user?.id || n.userId === user.id);
  
  const [notifications, setNotifications] = useState(
    userNotifications.map(n => {
      const iconConfig = ICON_MAP[n.type] || ICON_MAP.promo;
      return {
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        time: new Date(n.createdAt).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }),
        read: n.read,
        icon: iconConfig.icon,
        color: iconConfig.color,
      };
    })
  );
  const [filter, setFilter] = useState('all');

  const unreadCount = notifications.filter(n => !n.read).length;
  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read) 
    : notifications;

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  if (notifications.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
          <div className="flex items-center gap-4 p-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="notifications-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{t('notifications', 'notifications')}</h1>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 mt-20" data-testid="notifications-empty">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Bell className="w-12 h-12 text-gray-300" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('notifications', 'noNotifications')}</h2>
          <p className="text-gray-500 text-center">{t('notifications', 'noNotificationsYet')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden" data-testid="notifications-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('notifications', 'notifications')}</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-500">{unreadCount} {t('notifications', 'unread')}</p>
              )}
            </div>
          </div>
          <button 
            onClick={() => {/* Navigate to notification settings */}}
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="notifications-settings-btn"
          >
            <Settings className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="max-w-screen-2xl mx-auto flex gap-2 px-6 pb-3">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-all"
          >
            {t('notifications', 'all')}
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
              filter === 'unread' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-unread"
          >
            {t('notifications', 'unreadOnly')}
            {unreadCount > 0 && (
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                filter === 'unread' ? 'bg-white/20' : 'bg-emerald-500 text-white'
              }`}>
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Actions */}
        {unreadCount > 0 && (
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-6 pb-3 border-t border-gray-100 pt-3">
            <button 
              onClick={markAllAsRead}
              className="text-sm text-emerald-600 font-medium flex items-center gap-1 hover:text-emerald-700"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4" />
              {t('notifications', 'markAllAsRead')}
            </button>
            <button 
              onClick={clearAll}
              className="text-sm text-red-500 font-medium hover:text-red-600"
              data-testid="clear-all-btn"
            >
              {t('notifications', 'deleteAll')}
            </button>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6" data-testid="notifications-list">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
          {filteredNotifications.map((notification) => {
            const IconComponent = notification.icon;
            return (
              <div 
                key={notification.id} 
                className={`bg-white rounded-2xl p-4 lg:p-5 shadow-sm relative overflow-hidden hover:shadow-md transition-shadow ${
                  !notification.read ? 'border-l-4 border-emerald-500' : ''
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex gap-4">
                  <div className={`w-12 h-12 lg:w-14 lg:h-14 bg-${notification.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <IconComponent className={`w-6 h-6 lg:w-7 lg:h-7 text-${notification.color}-500`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`font-medium lg:text-lg ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                        {notification.title}
                      </h3>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-400">{notification.time}</span>
                      <div className="flex items-center gap-2">
                        {!notification.read && (
                          <button 
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs text-emerald-600 font-medium flex items-center gap-1 hover:text-emerald-700"
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <Check className="w-3 h-3" />
                            {t('notifications', 'read')}
                          </button>
                        )}
                        <button 
                          onClick={() => deleteNotification(notification.id)}
                          className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors"
                          data-testid={`delete-notification-${notification.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
